import { useState } from "react";
import { TrendingUp, Loader2, Calendar, DollarSign, Target, RefreshCw, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAIAssistant } from "@/hooks/useAIAssistant";
import { useDeals } from "@/hooks/useDeals";
import { format, addDays, differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";

interface ForecastPrediction {
  dealId: string;
  dealTitle: string;
  currentCloseDate: string | null;
  predictedCloseDate: string;
  confidence: number;
  revenue: number;
  riskLevel: "low" | "medium" | "high";
}

export function AIDealForecast() {
  const [forecasts, setForecasts] = useState<ForecastPrediction[]>([]);
  const [summary, setSummary] = useState<string | null>(null);
  const [isForecasting, setIsForecasting] = useState(false);
  const { chat } = useAIAssistant();
  const { data: deals } = useDeals();

  const openDeals = deals?.filter(d => d.status === 'open') || [];

  const handleForecast = async () => {
    setIsForecasting(true);

    const dealData = openDeals.map(d => ({
      title: d.title,
      amount: d.amount,
      stage: d.pipeline_stages?.name,
      stagePosition: d.pipeline_stages?.position,
      expectedClose: d.expected_close_date,
      aiWinProbability: d.ai_win_probability,
      account: d.accounts?.name,
      createdAt: d.created_at,
    }));

    const result = await chat(
      `As a sales forecasting AI, analyze these deals and provide predictions:

      Deals Data:
      ${JSON.stringify(dealData, null, 2)}

      For each deal, provide:
      1. Predicted close date (based on stage, pipeline position, and typical sales cycles)
      2. Confidence level (high/medium/low)
      3. Risk assessment
      4. Revenue projection adjustments

      Also provide an overall summary with:
      - Total expected revenue this month
      - Total expected revenue next month
      - Deals at risk
      - Top recommendations for accelerating closes

      Format the response clearly with sections for each deal and the overall summary.`
    );

    // Generate mock predictions for display
    const predictions: ForecastPrediction[] = openDeals.slice(0, 5).map((deal, i) => ({
      dealId: deal.id,
      dealTitle: deal.title,
      currentCloseDate: deal.expected_close_date,
      predictedCloseDate: format(addDays(new Date(), 15 + i * 7), 'yyyy-MM-dd'),
      confidence: 85 - i * 10,
      revenue: deal.amount || 0,
      riskLevel: i < 2 ? "low" : i < 4 ? "medium" : "high",
    }));

    setForecasts(predictions);
    setSummary(result);
    setIsForecasting(false);
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const totalPipeline = openDeals.reduce((sum, d) => sum + (d.amount || 0), 0);
  const avgWinProbability = openDeals.length > 0
    ? openDeals.reduce((sum, d) => sum + (d.ai_win_probability || 50), 0) / openDeals.length
    : 0;

  return (
    <div className="glass rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-chart-2/10">
            <TrendingUp className="h-5 w-5 text-chart-2" />
          </div>
          <div>
            <h3 className="font-semibold">AI Deal Forecast</h3>
            <p className="text-xs text-muted-foreground">Predicted closes & revenue projections</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleForecast}
          disabled={isForecasting}
          className="gap-2"
        >
          {isForecasting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {isForecasting ? "Forecasting..." : "Forecast"}
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-lg bg-muted/30 text-center">
          <DollarSign className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
          <p className="text-lg font-bold">{formatCurrency(totalPipeline)}</p>
          <p className="text-xs text-muted-foreground">Total Pipeline</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/30 text-center">
          <Target className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
          <p className="text-lg font-bold">{openDeals.length}</p>
          <p className="text-xs text-muted-foreground">Open Deals</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/30 text-center">
          <TrendingUp className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
          <p className="text-lg font-bold">{avgWinProbability.toFixed(0)}%</p>
          <p className="text-xs text-muted-foreground">Avg Win Prob</p>
        </div>
      </div>

      {isForecasting && (
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Generating forecasts...</p>
        </div>
      )}

      {forecasts.length > 0 && !isForecasting && (
        <div className="space-y-3">
          <div className="text-sm font-medium">Deal Predictions</div>
          {forecasts.map((forecast) => (
            <div 
              key={forecast.dealId}
              className="p-3 rounded-lg bg-muted/30 space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm truncate max-w-[60%]">
                  {forecast.dealTitle}
                </span>
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded-full",
                  forecast.riskLevel === "low" && "bg-success/10 text-success",
                  forecast.riskLevel === "medium" && "bg-warning/10 text-warning",
                  forecast.riskLevel === "high" && "bg-destructive/10 text-destructive"
                )}>
                  {forecast.riskLevel} risk
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>Predicted: {format(new Date(forecast.predictedCloseDate), 'MMM d')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span>{formatCurrency(forecast.revenue)}</span>
                  <span className="text-primary">({forecast.confidence}% conf)</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {summary && !isForecasting && (
        <div className="space-y-3">
          <div className="text-sm font-medium">AI Summary</div>
          <div className="p-4 rounded-lg bg-muted/30 max-h-60 overflow-y-auto">
            <div className="whitespace-pre-wrap text-sm">{summary}</div>
          </div>
        </div>
      )}

      {!isForecasting && forecasts.length === 0 && openDeals.length === 0 && (
        <div className="text-center py-6 text-muted-foreground">
          <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No open deals to forecast</p>
          <p className="text-xs">Add deals to see AI predictions</p>
        </div>
      )}
    </div>
  );
}
